import { useState, type ReactNode } from "react";
import Markdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { cx, segItem, segTrack } from "./ui.tsx";

/** Rendered markdown in the app's reading style. Links always open in a new tab. */
export function Md({ children, className }: { children: string; className?: string }) {
  return (
    <div className={cx("prose-krut", className)}>
      <Markdown remarkPlugins={[remarkGfm]} components={{ a: ({ href, children }) => <a href={href} target="_blank" rel="noreferrer noopener">{children}</a> }}>
        {children}
      </Markdown>
    </div>
  );
}

/** `---\nkey: value\n---` at the top of a markdown file (skills, notes), split from the body. */
export function splitFrontmatter(text: string): { meta: [string, string][]; body: string } {
  const m = /^---\s*\n([\s\S]*?)\n---\s*\n?/.exec(text);
  if (!m) return { meta: [], body: text };
  const meta: [string, string][] = [];
  let parent = "";
  for (const line of m[1]!.split("\n")) {
    const kv = /^(\s*)([\w.-]+):\s*(.*)$/.exec(line);
    if (!kv) continue;
    const [, indent, key, value] = kv;
    if (!indent) parent = key!;
    // Nested keys read as "metadata › hermes › tags"; empty parents only group their children.
    const label = indent ? `${parent} › ${key}` : key!;
    if (value!.trim()) meta.push([label, value!.trim().replace(/^["']|["']$/g, "")]);
  }
  return { meta, body: text.slice(m[0].length) };
}

/** A markdown document with its frontmatter shown as a tidy header, plus a toggle to the raw source. */
export function MarkdownDoc({ text, actions }: { text: string; actions?: ReactNode }) {
  const [raw, setRaw] = useState(false);
  const { meta, body } = splitFrontmatter(text);
  const description = meta.find(([k]) => k === "description")?.[1];
  const rest = meta.filter(([k]) => k !== "description" && k !== "name");
  return (
    <div>
      <div className="mb-3 flex items-center gap-2">
        <div className={segTrack}>
          {(["Rendered", "Source"] as const).map((l) => (
            <button key={l} onClick={() => setRaw(l === "Source")} className={segItem((l === "Source") === raw)}>
              {l}
            </button>
          ))}
        </div>
        <div className="ml-auto">{actions}</div>
      </div>
      {raw ? (
        <pre className="max-h-[60vh] overflow-auto whitespace-pre-wrap rounded-lg bg-[var(--panel-2)] px-4 py-3 font-mono text-xs leading-relaxed">{text}</pre>
      ) : (
        <>
          {(description || rest.length > 0) && (
            <div className="mb-4 rounded-lg border border-[var(--line)] bg-[var(--panel-2)] px-4 py-3">
              {description && <p className="text-sm">{description}</p>}
              {rest.length > 0 && (
                <dl className="mt-2 grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-xs">
                  {rest.map(([k, v]) => (
                    <div key={k} className="contents">
                      <dt className="text-[var(--muted)]">{k}</dt>
                      <dd className="min-w-0 break-words">{v.startsWith("[") && v.endsWith("]") ? (
                        <span className="flex flex-wrap gap-1">{v.replace(/^\[|\]$/g, "").split(/,\s*/).map((t) => <span key={t} className="rounded bg-[var(--panel)] px-1.5 py-0.5 ring-1 ring-[var(--line)]">{t}</span>)}</span>
                      ) : v}</dd>
                    </div>
                  ))}
                </dl>
              )}
            </div>
          )}
          <Md className="max-h-[60vh] overflow-y-auto pr-1">{body}</Md>
        </>
      )}
    </div>
  );
}
